import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Log In — BuildWise AI · San Diego Permit Platform"
        description="Log in to BuildWise AI to access your active projects, saved permit reports, and live AI chat pass. Continue where you left off."
        path="/login"
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-16 md:py-24">
        <div className="label-overline mb-4">Welcome back</div>
        <h1 className="font-display font-black text-4xl tracking-tight mb-4" style={{ color: "var(--bw-text-primary)" }}>
          Log in to BuildWise AI.
        </h1>
        <p className="text-sm mb-10 leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          Access your projects, saved permit reports, and dashboard. Your AI interview history, feasibility summaries,
          and premium report downloads are all one click away.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="label-overline block mb-2">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="bw-input" data-testid="login-email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="bw-input" data-testid="login-password"
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div className="text-sm p-3 border" style={{ color: "var(--bw-error)", borderColor: "var(--bw-error)" }} data-testid="login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
            {busy ? "Signing in…" : "Log in"}
          </button>
        </form>

        <div className="mt-10 pt-8 border-t" style={{ borderColor: "var(--bw-border)" }}>
          <div className="label-overline mb-3">Why BuildWise?</div>
          <ul className="text-sm space-y-2" style={{ color: "var(--bw-text-secondary)" }}>
            <li>· Instant feasibility for all 19 San Diego jurisdictions</li>
            <li>· AI-generated permit checklists, fees, and timelines</li>
            <li>· Downloadable PDF reports for lenders, contractors, and city counters</li>
            <li>· Optional concierge service that handles submissions end-to-end</li>
          </ul>
        </div>

        <p className="mt-8 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
          New here? <Link to="/signup" className="font-semibold underline" data-testid="link-signup">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
